/**
 * Decides which gender pool the next spin draws from.
 *
 * Pure and randomness-injected so the quota rules can be tested exhaustively
 * without a DOM, a network, or a real reel.
 *
 * @param reservedMale slots held back for male merit winners not yet awarded
 * @param reservedFemale the same for female merit winners
 * @param random () => number producing a float in [0, 1)
 * @returns {'M' | 'F' | null} null when nothing can be drawn
 */
export const pickGenderTrack = ({
                                    malePoolSize,
                                    femalePoolSize,
                                    maleWinners,
                                    femaleWinners,
                                    maxMaleWinners,
                                    maxFemaleWinners,
                                    reservedMale = 0,
                                    reservedFemale = 0,
                                    random,
                                }) => {
    // `held` slots are invisible to a spin drawn by chance: they belong to merit
    // winners who are not due yet. Without this, chance can fill a gender's quota
    // before its merit tickets come up, and the merit seat is silently lost.
    const choose = (heldMale, heldFemale) => {
        const maleSlots = maxMaleWinners - heldMale;
        const femaleSlots = maxFemaleWinners - heldFemale;

        const maleAvailable = malePoolSize > 0 && maleWinners < maleSlots;
        const femaleAvailable = femalePoolSize > 0 && femaleWinners < femaleSlots;

        if (!maleAvailable && !femaleAvailable) return null;
        if (!femaleAvailable) return 'M';
        if (!maleAvailable) return 'F';

        // Both sides still open: weight the coin by how many slots each has left, so
        // the quotas fill in an interleaved order rather than one gender back to back.
        const maleRemaining = maleSlots - maleWinners;
        const femaleRemaining = femaleSlots - femaleWinners;
        const femaleWeight = femaleRemaining / (maleRemaining + femaleRemaining);

        return random() < femaleWeight ? 'F' : 'M';
    };

    // Reservations shape the choice but must never stall the draw: if holding
    // every reserved slot leaves nothing to spin — a phase whose merit tickets
    // claim all the prizes still to be awarded — fall back to the plain quotas
    // and let the merit winners come out on their own turn.
    return choose(reservedMale, reservedFemale) ?? choose(0, 0);
};
